/**
 * card.js — Shared card component logic
 *
 * - Apply a style config from styles.json
 * - Text overflow fitting via transform: scaleX()
 * - Populate card from a data object (print page)
 * - CSS custom property updates (colour pickers)
 *
 * Page-specific code (form wiring, uploads, export) lives elsewhere.
 */

(function () {
  "use strict";

  /* ── Style application ──────────────────────────────────── */

  function applyStyle(styleConfig, root) {
    if (!styleConfig || !root) return;

    var cardEl = root.querySelector(".card");
    if (!cardEl) return;

    // Swap style class — only remove known style classes, preserve structural ones
    ["modern", "retro", "classic"].forEach(function (s) {
      cardEl.classList.remove("card--" + s);
    });
    cardEl.classList.add("card--" + styleConfig.id);

    // Set CSS custom properties
    var vars = styleConfig.css_variables || {};
    Object.keys(vars).forEach(function (prop) {
      root.style.setProperty(prop, vars[prop]);
    });

    // Set asset URLs
    _setAsset(root, "#embellishment-img", styleConfig.front && styleConfig.front.embellishment_image);
    _setAsset(root, "#badge-img", styleConfig.front && styleConfig.front.badge_image);
  }

  function loadAndApplyStyle(styleId, root, jsonPath) {
    jsonPath = jsonPath || "./styles.json";
    return fetch(jsonPath)
      .then(function (r) { return r.json(); })
      .then(function (config) {
        var sc = config.styles[styleId] || config.styles[config.defaultStyle];
        if (!sc) {
          console.warn("[card.js] Unknown style: " + styleId);
          return null;
        }
        applyStyle(sc, root);
        return sc;
      });
  }

  /* ── CSS custom property helper ─────────────────────────── */

  function setCssVar(root, prop, value) {
    if (root && prop && value) root.style.setProperty(prop, value);
  }

  /* ── Text auto-sizing ───────────────────────────────────── */

  function fitNameText(spanEl) {
    if (!spanEl) return;
    spanEl.style.transform = "";
    var row = spanEl.parentElement;
    if (!row) return;
    var cs = window.getComputedStyle(row);
    var padL = parseFloat(cs.paddingLeft) || 0;
    var padR = parseFloat(cs.paddingRight) || 0;
    var containerW = row.offsetWidth - padL - padR;
    var textW = spanEl.scrollWidth;
    var scale = (textW > 0 && containerW > 0 && textW > containerW)
      ? containerW / textW : 1;
    spanEl.style.setProperty("--fit-scale", scale);
    spanEl.style.transform = "scaleX(" + scale + ")";
    spanEl.style.transformOrigin = "left center";
  }

  function fitAllNameText(root) {
    (root || document)
      .querySelectorAll(".card__player-name, .card__team-name")
      .forEach(fitNameText);
  }

  function animateTextUpdate(root) {
    var cardEl = root && root.querySelector(".card");
    if (!cardEl) return;
    cardEl.classList.remove("card--animating");
    void cardEl.offsetWidth;
    cardEl.classList.add("card--animating");
    cardEl.addEventListener("animationend", function () {
      cardEl.classList.remove("card--animating");
    }, { once: true });
    fitAllNameText(root);
  }

  /* ── Card population (print page) ───────────────────────── */

  function populateCard(data, root) {
    if (!data || !root) return;

    // Front text
    var fullName = [data.FName, data.LName].filter(Boolean).join(" ");
    _setText(root, "#player-name", fullName);
    _setText(root, "#team-name", data.TName);

    // Front images
    _setImg(root, "#player-photo", data.frontImg);
    _setImg(root, "#team-logo", data.logoImg);

    // Colours
    if (data.ColourT) setCssVar(root, "--text-colour", data.ColourT);
    if (data.Colour1) setCssVar(root, "--primary-colour", data.Colour1);
    if (data.Colour2) setCssVar(root, "--secondary-colour", data.Colour2);

    // Back face (if elements exist)
    _setText(root, "#player-name-back", fullName);
    _setText(root, "#team-name-back", data.TName);
    _setText(root, "#player-position", data.Position);
    _setText(root, "#player-number", data.Number);
    _setText(root, "#player-height", data.Height);
    _setText(root, "#player-weight", data.Weight);
    _setText(root, "#hand-value", data.Handedness);
    _setText(root, "#player-hometown", data.Hometown);
    _setText(root, "#player-bio-value", data["Custom Message"] || "");
    _setImg(root, "#back-player-image", data.backImg || data.frontImg);
    _setImg(root, "#logoBack1Img", data.logoback1Img);
    _setImg(root, "#logoBack2Img", data.logoback2Img);

    // Birthday
    if (data.Birthday) {
      var d = new Date(data.Birthday + "T00:00:00");
      if (!isNaN(d)) {
        _setText(root, "#player-birth", d.toLocaleDateString("en-US", {
          month: "short", day: "numeric", year: "numeric"
        }));
      }
    }

    // Stats
    if (data.StatData) {
      data.StatData.split("|").forEach(function (val, i) {
        _setText(root, "#card-stat-" + (i + 1) + "-preview", val.trim());
      });
    }

    fitAllNameText(root);
  }

  /* ── Query string parser ────────────────────────────────── */

  function parseQueryString(qs) {
    qs = qs !== undefined ? qs : window.location.search;
    if (!qs) return {};
    var params = new URLSearchParams(qs);
    if (params.has("_design_data")) {
      return parseQueryString("?" + params.get("_design_data"));
    }
    var result = {};
    params.forEach(function (value, key) { result[key] = value; });
    return result;
  }

  /* ── Private helpers ────────────────────────────────────── */

  function _setText(root, sel, val) {
    var el = root.querySelector(sel);
    if (el && val != null) el.textContent = val;
  }

  function _setImg(root, sel, src) {
    var el = root.querySelector(sel);
    if (el && src) el.src = src;
  }

  function _setAsset(root, sel, src) {
    if (src) _setImg(root, sel, src);
  }

  /* ── Public API ─────────────────────────────────────────── */

  window.CardComponent = {
    applyStyle:        applyStyle,
    loadAndApplyStyle: loadAndApplyStyle,
    setCssVar:         setCssVar,
    fitNameText:       fitNameText,
    fitAllNameText:    fitAllNameText,
    animateTextUpdate: animateTextUpdate,
    populateCard:      populateCard,
    parseQueryString:  parseQueryString,
  };

}());
